import { oxAuthHeaders } from './guestId.js';

const KEY = 'ox-recent-assets';
/** Asset ids the user removed from "recientes" — survives refresh even if API is stale. */
const DISMISSED_KEY = 'ox-recent-assets-dismissed';

export type RecentAsset = {
  assetId: string;
  thumbUrl: string;
  createdAt: number;
};

function loadDismissedIds(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as string[];
    return new Set(Array.isArray(parsed) ? parsed.filter(Boolean) : []);
  } catch {
    return new Set();
  }
}

function saveDismissedIds(ids: Set<string>): void {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids].slice(0, 200)));
  } catch {
    /* ignore quota */
  }
}

export function dismissRecentAsset(assetId: string): void {
  if (!assetId) return;
  const ids = loadDismissedIds();
  ids.add(assetId);
  saveDismissedIds(ids);
}

export function undismissRecentAsset(assetId: string): void {
  if (!assetId) return;
  const ids = loadDismissedIds();
  if (!ids.delete(assetId)) return;
  saveDismissedIds(ids);
}

export function loadRecentAssets(): RecentAsset[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RecentAsset[];
    if (!Array.isArray(parsed)) return [];
    const dismissed = loadDismissedIds();
    return parsed
      .filter((a) => a?.assetId && !dismissed.has(a.assetId))
      .slice(0, 24);
  } catch {
    return [];
  }
}

export function pushRecentAsset(entry: RecentAsset): void {
  try {
    undismissRecentAsset(entry.assetId);
    const prev = loadRecentAssets().filter((a) => a.assetId !== entry.assetId);
    localStorage.setItem(KEY, JSON.stringify([entry, ...prev].slice(0, 24)));
  } catch {
    /* ignore quota */
  }
}

export function removeRecentAsset(assetId: string): void {
  try {
    dismissRecentAsset(assetId);
    localStorage.setItem(
      KEY,
      JSON.stringify(loadRecentAssets().filter((a) => a.assetId !== assetId)),
    );
  } catch {
    /* ignore */
  }
}

/**
 * Remove from the owner's library: local list + server archive (soft delete).
 * Designs that already use the asset keep working via GET /assets/:id.
 */
export async function deleteOwnerAsset(
  apiBase: string,
  assetId: string,
  opts?: { token?: string | null },
): Promise<void> {
  removeRecentAsset(assetId);
  const base = apiBase.replace(/\/$/, '');
  try {
    const res = await fetch(`${base}/assets/${encodeURIComponent(assetId)}`, {
      method: 'DELETE',
      headers: oxAuthHeaders(opts?.token),
    });
    if (!res.ok && res.status !== 404) {
      // Local dismiss still sticks; list won't resurrect on refresh.
      console.warn('[ox] delete asset failed', res.status);
    }
  } catch (err) {
    console.warn('[ox] delete asset network error', err);
  }
}

/**
 * Load owner's asset library from the API (server source of truth).
 * Falls back to / merges with localStorage cache (covers guest vs JWT races and optimistic uploads).
 * Honors local dismissals so deleted rows never reappear after refresh.
 */
export async function fetchOwnerAssets(
  apiBase: string,
  opts?: { token?: string | null; limit?: number },
): Promise<RecentAsset[]> {
  const base = apiBase.replace(/\/$/, '');
  const limit = opts?.limit ?? 24;
  const local = loadRecentAssets();
  const dismissed = loadDismissedIds();
  try {
    const res = await fetch(`${base}/assets?limit=${limit}`, {
      headers: oxAuthHeaders(opts?.token),
    });
    if (!res.ok) return local;
    const data = (await res.json()) as {
      items?: Array<{ id: string; thumbUrl?: string; proxyUrl?: string; createdAt?: string }>;
    };
    const items = Array.isArray(data.items) ? data.items : [];
    const fromServer: RecentAsset[] = items
      .filter((r) => r?.id && !dismissed.has(r.id))
      .map((r) => ({
        assetId: r.id,
        thumbUrl: r.thumbUrl || r.proxyUrl || '',
        createdAt: r.createdAt ? Date.parse(r.createdAt) || Date.now() : Date.now(),
      }));
    const seen = new Set(fromServer.map((a) => a.assetId));
    const merged = [
      ...fromServer,
      ...local.filter((a) => a.assetId && !seen.has(a.assetId) && !dismissed.has(a.assetId)),
    ].slice(0, limit);
    try {
      localStorage.setItem(KEY, JSON.stringify(merged.slice(0, 24)));
    } catch {
      /* ignore */
    }
    return merged;
  } catch {
    return local;
  }
}
